import { styled } from 'styled-components';
import { NavLink } from 'react-router-dom';
import {
	handleColorType,
	mediaQueries,
	disableUserSelect,
	fadeInTransition,
	media,
	ResponsiveTextSize,
} from '@quizzach/styles';
import { motion } from 'framer-motion';

export const BackgroundContainer = styled(motion.div)<{
	color: string;
	$backgroundImage?: string;
}>`
	background-color: ${({ color, theme }) => handleColorType(color, theme)};
	background-image: ${({ $backgroundImage }) =>
		$backgroundImage ? `${$backgroundImage}` : 'none'};
	width: 100vw;
	position: relative;

	background-repeat: no-repeat;
	background-size: 100% auto;
	background-position: top center;
	padding-bottom: 70px;

	${mediaQueries.mediaWidth} {
		min-height: 100svh;
		display: flex;
		flex-direction: column;
	}

	${mediaQueries.desktopWidth} {
		min-height: unset;
	}

	.noselect {
		${disableUserSelect}
	}

	.flex-column {
		display: flex;
		flex-direction: column;
	}
`;

export const Container = styled.div`
	${mediaQueries.mediaWidth} {
		padding: 10px;
		display: flex;
		flex-direction: column;
		flex: 1;
	}

	${mediaQueries.tabletWidth} {
		padding: 10px;
	}
	${mediaQueries.desktopWidth} {
		max-width: 1900px;
		margin: 0px auto;
		min-height: calc(100vh - 95px);
	}

	.eye-icon {
		position: absolute;
		right: 10px;
		top: 50%;
		transform: translateY(-50%);
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
	}
	.validation-alert {
		margin: 0;
		padding: 0;
		background-color: transparent;
		gap: 5px;

		.alert-icon {
			svg {
				width: 15px;
			}
		}
	}
`;

export const H2 = styled(motion.h2)<{ color: string }>`
	all: unset;

	font-family: ${({ theme }) => theme.typography.fontPrimary};
	font-weight: 700;
	color: ${({ color, theme }) => handleColorType(color, theme)};
	text-transform: uppercase;
	display: block;

	font-size: 30px;
	line-height: 115%;
	margin: 10px auto;

	${media.mobile} {
		font-size: 35px;
		line-height: 115%;
	}
	${media.tablet} {
		font-size: 45px;
		line-height: 115%;
	}
	${media.largeTablet} {
		font-size: 50px;
		line-height: 115%;
	}
	${media.smallDesktop} {
		font-size: 55px;
		line-height: 115%;
		margin: 16px 0;
	}
	${media.largeDesktop} {
		font-size: 60px;
		line-height: 120%;
	}
	${media.extraLargeDesktop} {
		font-size: 65px;
		line-height: 130%;
	}
	${media.ultrawide} {
		font-size: 70px;
		line-height: 130%;
	}
`;
export const H3 = styled.h3<{ color: string }>`
	all: unset;

	font-family: ${({ theme }) => theme.typography.fontPrimary};
	font-weight: 500;

	color: ${({ color, theme }) => handleColorType(color, theme)};
	text-transform: uppercase;
	margin: 10px auto;
	display: block;

	font-size: 25px;
	line-height: 115%;

	${media.mobile} {
		font-size: 30px;
		line-height: 115%;
	}
	${media.tablet} {
		font-size: 40px;
		line-height: 115%;
	}
	${media.largeTablet} {
		font-size: 45px;
		line-height: 115%;
	}
	${media.smallDesktop} {
		font-size: 45px;
		line-height: 115%;
		margin: 16px 0px;
	}
	${media.largeDesktop} {
		font-size: 50px;
		line-height: 120%;
	}
	${media.extraLargeDesktop} {
		font-size: 55px;
		line-height: 130%;
	}
	${media.ultrawide} {
		font-size: 60px;
		line-height: 130%;
	}
`;

export const H4 = styled.h4<{ color: string }>`
	all: unset;

	font-family: ${({ theme }) => theme.typography.fontPrimary};
	color: ${({ color, theme }) => handleColorType(color, theme)};
	text-transform: uppercase;
	margin: 10px auto;
	display: block;
	font-weight: 400;
	font-size: 17px;
	line-height: 115%;

	${media.mobile} {
		font-size: 20px;
		line-height: 115%;
	}
	${media.tablet} {
		font-size: 25px;
		line-height: 115%;
	}
	${media.largeTablet} {
		font-size: 30px;
		line-height: 115%;
	}
	${media.smallDesktop} {
		font-size: 30px;
		line-height: 115%;
		margin: 16px 0px;
	}
	${media.largeDesktop} {
		font-size: 30px;
		line-height: 120%;
	}
	${media.extraLargeDesktop} {
		font-size: 35px;
		line-height: 130%;
	}
`;

export const H5 = styled(motion.h5)<{ color: string }>`
	all: unset;

	font-family: ${({ theme }) => theme.typography.fontPrimary};
	color: ${({ color, theme }) => handleColorType(color, theme)};
	text-transform: uppercase;
	display: block;

	font-size: 17px;
	line-height: 115%;

	${media.mobile} {
		font-size: 20px;
	}
	${media.tablet} {
		font-size: 25px;
	}
	${media.largeTablet} {
		font-size: 25px;
	}
	${media.smallDesktop} {
		font-size: 25px;
		line-height: 115%;
	}
	${media.largeDesktop} {
		font-size: 25px;
		line-height: 120%;
	}
	${media.extraLargeDesktop} {
		font-size: 30px;
		line-height: 130%;
	}
`;

export const Text = styled(motion.p)<{
	color: string;
	$size?: 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl' | 'xxxl';
	$alternative?: boolean;
}>`
	font-family: ${({ $alternative, theme }) =>
		$alternative
			? theme.typography.fontPrimary
			: theme.typography.fontSecondary};
	color: ${({ color, theme }) => handleColorType(color, theme)};
	font-weight: 500;
	margin: 10px auto;

	font-size: ${({ $size }) =>
		$size ? ResponsiveTextSize.mobile[$size] : '12px'};
	line-height: 130%;

	${media.tablet} {
		font-size: ${({ $size }) =>
			$size ? ResponsiveTextSize.tablet[$size] : '14px'};
		line-height: 130%;
	}
	${media.largeTablet} {
		font-size: ${({ $size }) =>
			$size ? ResponsiveTextSize.largeTablet[$size] : '16px'};
		line-height: 130%;
	}
	${media.smallDesktop} {
		font-size: ${({ $size }) =>
			$size ? ResponsiveTextSize.smallDesktop[$size] : '16px'};
		line-height: 130%;
	}
	${media.largeDesktop} {
		font-size: ${({ $size }) =>
			$size ? ResponsiveTextSize.largeDesktop[$size] : '16px'};
		line-height: 130%;
	}
	${media.extraLargeDesktop} {
		font-size: ${({ $size }) =>
			$size ? ResponsiveTextSize.extraLargeDesktop[$size] : '16px'};
		line-height: 130%;
	}
	${media.ultrawide} {
		font-size: ${({ $size }) =>
			$size ? ResponsiveTextSize.ultrawide[$size] : '16px'};
		line-height: 130%;
	}
`;

export const HandwrittenText = styled.div<{
	color: string;
	$size?: number;
	$linearGradient?: boolean;
}>`
	font-family: ${({ theme }) => theme.typography.fontHandwritten};

	font-size: ${({ $size }) => ($size ? `${$size}px` : `40px`)};
	line-height: ${({ $size }) => ($size ? `${$size * 1.3}px` : `40px`)};

	${media.smallDesktop} {
		font-size: ${({ $size }) => ($size ? `${$size}px` : `70px`)};
		line-height: ${({ $size }) => ($size ? `${$size * 1.3}px` : `70px`)};
	}

	color: ${({ color, $linearGradient, theme }) =>
		$linearGradient ? 'transparent' : handleColorType(color, theme)};
	background: ${({ $linearGradient }) =>
		$linearGradient
			? `linear-gradient(
					270deg,
					#656565,
					#d0d0d0,
					#656565,
					#d0d0d0
				)`
			: 'none'};
	background-size: ${({ $linearGradient }) =>
		$linearGradient ? '800% 800%' : 'auto'};
	-webkit-background-clip: ${({ $linearGradient }) =>
		$linearGradient ? 'text' : 'none'};
	-webkit-text-fill-color: ${({ $linearGradient }) =>
		$linearGradient ? 'transparent' : 'initial'};

	font-weight: 500;
`;

export const Button = styled(motion.button)<{
	$big?: boolean;
	$small?: boolean;
	$fixedWidth?: number;
}>`
	all: unset;

	${disableUserSelect}

	font-family: ${({ theme }) => theme.typography.fontSecondary};
	background-color: ${({ theme }) => theme.palette.brand.main};
	color: ${({ theme }) => theme.palette.black};
	border: none;
	border-radius: var(--r-pill);
	max-width: 600px;
	width: 100%;
	margin: 4px;
	box-sizing: border-box;
	text-align: center;
	font-weight: 400;
	display: inline-block;
	line-height: 120%;

	&:disabled {
		background-color: ${({ theme }) => theme.palette.surface.soft};
		border: none;

		cursor: not-allowed;
		&:hover {
			background-color: ${({ theme }) => theme.palette.surface.soft};
			color: ${({ theme }) => theme.palette.black};
		}
	}
	padding: ${({ $small }) => ($small ? '7px 15px' : ' 10px 15px')};

	font-size: ${({ $small }) =>
		$small ? ResponsiveTextSize.mobile.xs : ResponsiveTextSize.mobile.s};

	${media.tablet} {
		font-size: ${({ $small }) =>
			$small ? ResponsiveTextSize.tablet.xs : ResponsiveTextSize.tablet.s};
	}
	${media.largeTablet} {
		font-size: ${({ $small }) =>
			$small
				? ResponsiveTextSize.largeTablet.xs
				: ResponsiveTextSize.largeTablet.s};
	}
	${media.smallDesktop} {
		font-size: ${({ $small }) =>
			$small
				? ResponsiveTextSize.smallDesktop.s
				: ResponsiveTextSize.smallDesktop.m};
		padding: ${({ $small }) => ($small ? '7px 15px' : '11px 20px')};
		max-width: unset;
		width: ${({ $fixedWidth }) => ($fixedWidth ? `${$fixedWidth}px` : '100%')};
		cursor: pointer;

		&:hover {
			background-color: ${({ theme }) => theme.palette.brand.pale};
			color: ${({ theme }) => theme.palette.text.white};
			transition: 0.3s;
		}
	}
	${media.largeDesktop} {
		padding: ${({ $small }) => ($small ? '5px 15px' : '11px 25px')};

		font-size: ${({ $small }) =>
			$small
				? ResponsiveTextSize.largeDesktop.s
				: ResponsiveTextSize.largeDesktop.m};
	}
	${media.extraLargeDesktop} {
		font-size: ${({ $small }) =>
			$small
				? ResponsiveTextSize.extraLargeDesktop.s
				: ResponsiveTextSize.extraLargeDesktop.m};
	}
`;

export const MailTo = styled.a<{ $clean?: boolean }>`
	all: unset;
	color: ${({ theme, $clean }) =>
		$clean ? theme.palette.text.main : theme.palette.brand.main};
	cursor: pointer;
	font-weight: ${({ $clean }) => ($clean ? 600 : 500)};

	&:hover {
		color: ${({ theme }) => theme.palette.brand.pale};
		transition: 0.3s;
	}
`;

export const TextLink = styled(NavLink)<{
	$size?: 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl';
	color?: string;
}>`
	all: unset;

	font-family: ${({ theme }) => theme.typography.fontSecondary};
	color: ${({ color, theme }) =>
		color ? handleColorType(color, theme) : theme.palette.brand.pale};
	font-weight: 500;
	margin: 10px 0px;
	&:hover {
		color: ${({ theme }) => theme.palette.brand.main};
		transition: 0.3s;
		cursor: pointer;
	}

	font-size: ${({ $size }) =>
		$size ? ResponsiveTextSize.mobile[$size] : '12px'};
	line-height: 115%;

	${media.tablet} {
		font-size: ${({ $size }) =>
			$size ? ResponsiveTextSize.tablet[$size] : '14px'};
	}
	${media.largeTablet} {
		font-size: ${({ $size }) =>
			$size ? ResponsiveTextSize.largeTablet[$size] : '16px'};
	}
	${media.smallDesktop} {
		font-size: ${({ $size }) =>
			$size ? ResponsiveTextSize.smallDesktop[$size] : '16px'};
	}
	${media.largeDesktop} {
		font-size: ${({ $size }) =>
			$size ? ResponsiveTextSize.largeDesktop[$size] : '16px'};
	}
	${media.extraLargeDesktop} {
		font-size: ${({ $size }) =>
			$size ? ResponsiveTextSize.extraLargeDesktop[$size] : '16px'};
	}
	${media.ultrawide} {
		font-size: ${({ $size }) =>
			$size ? ResponsiveTextSize.ultrawide[$size] : '16px'};
	}
`;

export const Checkbox = styled.label<{ $big?: boolean }>`
	${disableUserSelect}

	&:hover input ~ .checkmark {
		background-color: transparent;
	}

	input:checked ~ .checkmark {
		background-color: #00ffc2;
	}

	.checkmark:after {
		content: '';
		position: absolute;
		display: none;
	}

	input:checked ~ .checkmark:after {
		display: block;
	}

	input {
		position: absolute;
		opacity: 0;
		cursor: pointer;
		height: 0;
		width: 0;
	}
	&.disabled {
		cursor: not-allowed;

		.checkmark {
			opacity: 0.3;
		}
		input {
			cursor: not-allowed;
		}
	}

	${mediaQueries.desktopWidth} {
		display: block;
		position: relative;
		// margin-top: 20px;
		padding-left: 35px;
		margin-bottom: 12px;

		.checkmark {
			position: absolute;
			top: 0;
			left: 0;
			height: 25px;
			width: 25px;
			background-color: transparent;
			color: red;
			border: 1px solid #00ffc2;
			border-radius: var(--r-xs);
			transition: background-color 0.5s;
		}

		.checkmark:after {
			left: 8px;
			top: 3px;
			width: 6px;
			height: 14px;
			border: solid black;
			border-width: 0 1px 1px 0;
			-webkit-transform: rotate(45deg);
			-ms-transform: rotate(45deg);
			transform: rotate(45deg);
		}
	}
	${mediaQueries.mediaWidth} {
		display: block;
		position: relative;
		margin-top: 10px;
		padding-left: 25px;
		margin-bottom: 12px;

		.checkmark {
			position: absolute;
			top: 0;
			left: 0;
			height: ${({ $big }) => ($big ? '25px' : '16px')};
			width: ${({ $big }) => ($big ? '25px' : '16px')};
			background-color: transparent;
			color: red;
			border: 1px solid #00ffc2;
			border-radius: var(--r-xs);
			transition: background-color 0.5s;
		}

		.checkmark:after {
			left: ${({ $big }) => ($big ? '8px' : '4px')};
			top: 1px;
			width: ${({ $big }) => ($big ? '7px' : '5px')};
			height: ${({ $big }) => ($big ? '17px' : '10px')};
			border: solid black;
			border-width: 0 1px 1px 0;
			-webkit-transform: rotate(45deg);
			-ms-transform: rotate(45deg);
			transform: rotate(45deg);
		}
	}
`;

type AlertSeverity = 'error' | 'warning' | 'info';

// error = coral, info = active teal; warning gold flips to gold-ink on
// cream since gold only sits on dark surfaces in the redesign
const alertAccent = (
	type: AlertSeverity | undefined,
	mode: 'light' | 'dark'
) =>
	type === 'warning'
		? mode === 'dark'
			? 'var(--gold)'
			: 'var(--gold-ink)'
		: type === 'info'
			? 'var(--teal-dark)'
			: 'var(--coral)';

// soft severity wash behind the whole alert (borderless modern surface)
const alertTint = (type: AlertSeverity | undefined, mode: 'light' | 'dark') =>
	type === 'warning'
		? mode === 'dark'
			? 'rgba(219, 177, 84, 0.13)'
			: 'rgba(168, 124, 31, 0.11)'
		: type === 'info'
			? mode === 'dark'
				? 'rgba(0, 255, 194, 0.1)'
				: 'rgba(0, 128, 97, 0.1)'
			: mode === 'dark'
				? 'rgba(255, 123, 87, 0.13)'
				: 'rgba(241, 107, 70, 0.11)';

export const Alert = styled.div<{
	type?: AlertSeverity;
	$fixed?: boolean;
}>`
	${fadeInTransition}

	position: relative;
	display: flex;
	align-items: center;
	gap: 14px;
	/* borderless soft-tint surface: the severity wash + icon carry the
	   meaning, no stripes or outlines */
	background: ${({ type, theme }) => alertTint(type, theme.mode)};
	border: none;
	/* fixed pill-like radius (half a one-liner's height, like the island
	   bar's explicit 29px) - identical on single and multi-line alerts */
	border-radius: 27px;
	padding: 15px 22px;
	margin: 6px 0px;
	box-sizing: border-box;
	color: var(--ink);
	text-align: left;

	.alert-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		color: ${({ type, theme }) => alertAccent(type, theme.mode)};
	}
	.alert-title {
		font-family: var(--fd);
		text-transform: uppercase;
		font-size: 15px;
		margin-bottom: 4px;
	}
	.alert-text {
		font-family: var(--fb);
		font-weight: 500;
		font-size: 14px;
		line-height: 1.5;
		text-align: left;
	}

	${media.smallDesktop} {
		max-width: ${({ $fixed }) => $fixed && '340px'};
		padding: 15px 30px 15px 34px;
		width: fit-content;
		gap: 14px;

		&::before {
			left: 16px;
			top: 14px;
			bottom: 14px;
		}

		.alert-text {
			font-size: 15.5px;
		}
	}
`;

export const InlineAlert = styled.div<{
	type: AlertSeverity;
	$fixed?: boolean;
}>`
	${fadeInTransition}

	width: 100%;
	margin: 16px 0px;
	box-sizing: border-box;
	display: flex;
	gap: 9px;
	align-items: center;
	justify-content: flex-start;
	text-align: left;

	.alert-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		color: ${({ type, theme }) => alertAccent(type, theme.mode)};
	}
	.alert-text {
		font-family: var(--fb);
		font-weight: 500;
		font-size: 14px;
		line-height: 1.45;
		color: var(--brown);
	}

	${media.smallDesktop} {
		max-width: ${({ $fixed }) => $fixed && '340px'};
		width: fit-content;
		gap: 10px;
	}
`;

export const TextareaOutlined = styled.textarea`
	font-family: ${({ theme }) => theme.typography.fontSecondary};
	background-color: transparent;
	color: ${({ theme }) => theme.palette.text.main};
	outline: none;
	border-color: ${({ theme }) => theme.palette.surface.main};

	font-weight: 500;
	border-radius: var(--r-card);
	box-sizing: border-box;
	font-size: 14px;
	resize: none;

	&:focus {
		border: 1px solid ${({ theme }) => theme.palette.brand.main};
		font-size: 14px;
		color: ${({ theme }) => theme.palette.text.main};
	}

	&::placeholder {
		font-size: 14px;
	}
	padding: 20px;
	width: 100%;
	margin: 10px auto;

	${media.smallDesktop} {
		text-align: left;
		margin: 10px 0px;
	}
`;

export const OptionsBox = styled.div`
	display: flex;
	align-items: center;
	padding: 10px 0px;
	margin: 10px auto;
	gap: 5px;
	width: 100%;
	max-width: 600px;
	justify-content: flex-start;
	flex-wrap: wrap;

	a {
		all: unset;
		cursor: pointer;
		display: flex;
		align-items: center;
		background-color: ${({ theme }) => theme.palette.surface.brave};
		border-radius: var(--r-card);
		padding: 5px 10px;
		height: 20px;
	}

	${media.smallDesktop} {
		padding: 10px 0px;
		margin: 30px auto;
		width: max-content;
		max-width: max-content;
		gap: 10px;

		a {
			padding: 5px 10px;
			height: 30px;
		}
	}
`;
